import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CreateMemoModal - Admin modal for creating memos manually
 *
 * Allows admins to:
 * - Select a contact from phonebook
 * - Search and select a diamond from inventory
 * - Set memo details (purpose, due date, client info)
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes, FaSearch, FaGem, FaUser } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import { activityTracker } from '../services/activityTracker';
import { notify } from '../utils/notifications';
import mockDataService from '../services/mockDataService';
const Overlay = styled.div `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
`;
const Modal = styled.div `
    background: var(--bg-primary);
    border-radius: 12px;
    width: 100%;
    max-width: 700px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border-color);
`;
const Header = styled.div `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 3px solid var(--accent-gold);
    background: var(--bg-secondary);
`;
const Title = styled.h2 `
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
`;
const CloseButton = styled.button `
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 0.5rem;
    font-size: 1.25rem;

    &:hover {
        color: var(--text-primary);
    }
`;
const Content = styled.div `
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
`;
const Section = styled.div `
    margin-bottom: 1.5rem;

    &:last-child {
        margin-bottom: 0;
    }
`;
const SectionTitle = styled.h3 `
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
        color: var(--accent-gold);
    }
`;
const FormGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;
const FormGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const Label = styled.label `
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-secondary);
`;
const Input = styled.input `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }

    &::placeholder {
        color: var(--text-tertiary);
    }
`;
const Select = styled.select `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }
`;
const Textarea = styled.textarea `
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    resize: vertical;
    min-height: 80px;

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
    }

    &::placeholder {
        color: var(--text-tertiary);
    }
`;
const SearchContainer = styled.div `
    position: relative;
`;
const SearchInput = styled(Input) `
    padding-left: 2.5rem;
    width: 100%;
`;
const SearchIcon = styled.div `
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-tertiary);
`;
const Dropdown = styled.div `
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
    margin-top: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;
const DropdownItem = styled.div `
    padding: 0.75rem 1rem;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: var(--bg-tertiary);
    }

    .name {
        font-weight: 500;
        color: var(--text-primary);
    }

    .detail {
        font-size: 0.75rem;
        color: var(--text-tertiary);
    }
`;
const SelectedItem = styled.div `
    padding: 0.75rem 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--accent-gold);
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .info {
        .name {
            font-weight: 500;
            color: var(--text-primary);
        }
        .detail {
            font-size: 0.75rem;
            color: var(--text-tertiary);
        }
    }

    button {
        background: none;
        border: none;
        color: var(--text-tertiary);
        cursor: pointer;
        padding: 0.25rem;

        &:hover {
            color: #ef4444;
        }
    }
`;
const Footer = styled.div `
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--border-color);
    background: var(--bg-secondary);
`;
const Button = styled.button `
    padding: 0.625rem 1.25rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    ${props => props.primary ? `
        background: var(--accent-gold);
        border: 1px solid var(--accent-gold);
        color: white;

        &:hover:not(:disabled) {
            background: var(--accent-gold-dark);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    ` : `
        background: var(--bg-tertiary);
        border: 1px solid var(--border-color);
        color: var(--text-primary);

        &:hover {
            background: var(--bg-secondary);
        }
    `}
`;
const LookupButton = styled.button `
    padding: 0.625rem 1rem;
    background: var(--accent-gold);
    border: 1px solid var(--accent-gold);
    color: white;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: var(--accent-gold-dark);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;
const DiamondPreview = styled.div `
    padding: 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--accent-gold);
    border-radius: 6px;
    margin-top: 0.5rem;

    .title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 0.5rem;
    }

    .details {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.5rem;
        font-size: 0.8rem;

        span {
            color: var(--text-secondary);
        }
    }

    .price {
        margin-top: 0.75rem;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--accent-gold);
    }
`;
export const CreateMemoModal = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [contactSearch, setContactSearch] = useState('');
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [stockNumber, setStockNumber] = useState('');
    const [diamond, setDiamond] = useState(null);
    const [lookingUp, setLookingUp] = useState(false);
    const [clientName, setClientName] = useState('');
    const [clientContact, setClientContact] = useState('');
    const [purpose, setPurpose] = useState('viewing');
    const [dueDate, setDueDate] = useState('');
    const [notes, setNotes] = useState('');
    useEffect(() => {
        if (isOpen) {
            fetchContacts();
        }
    }, [isOpen]);
    const fetchContacts = async () => {
        try {
            if (mockDataService.isMockMode()) {
                setContacts(mockDataService.getContacts());
            }
            else {
                const response = await apiClient.get('contacts-list');
                if (response.ok && response.data?.contacts) {
                    setContacts(response.data.contacts);
                }
            }
        }
        catch (error) {
            console.error('Error fetching contacts:', error);
        }
    };
    const filteredContacts = contacts.filter(c => {
        if (!contactSearch)
            return false;
        const search = contactSearch.toLowerCase();
        return (c.companyName?.toLowerCase().includes(search) ||
            c.contactPerson?.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search));
    });
    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setContactSearch('');
        setShowContactDropdown(false);
    };
    const handleLookupDiamond = async () => {
        if (!stockNumber.trim()) {
            notify.error('Error', 'Please enter a stock number');
            return;
        }
        setLookingUp(true);
        try {
            if (mockDataService.isMockMode()) {
                // Mock lookup - simulate finding a diamond
                setDiamond({
                    id: `diamond-${stockNumber}`,
                    stockNumber: stockNumber,
                    shape: 'Round Brilliant',
                    caratWeight: 1.5,
                    color: 'G',
                    clarity: 'VS1',
                    cut: 'Excellent',
                    lab: 'GIA',
                    certificateNumber: `GIA${Math.random().toString().slice(2, 10)}`,
                    priceUSD: 8500
                });
                notify.success('Found', 'Diamond found in inventory');
            }
            else {
                const response = await apiClient.get(`diamonds-get/${stockNumber}`);
                if (response.ok && response.data?.diamond) {
                    setDiamond(response.data.diamond);
                    notify.success('Found', 'Diamond found in inventory');
                }
                else {
                    notify.error('Not Found', 'Diamond not found in inventory');
                }
            }
        }
        catch (error) {
            notify.error('Error', 'Failed to lookup diamond');
        }
        finally {
            setLookingUp(false);
        }
    };
    const handleSubmit = async () => {
        if (!selectedContact) {
            notify.error('Error', 'Please select a contact');
            return;
        }
        if (!diamond) {
            notify.error('Error', 'Please lookup and select a diamond');
            return;
        }
        setLoading(true);
        try {
            const memoData = {
                dealerId: selectedContact.id,
                dealerEmail: selectedContact.email,
                dealerName: selectedContact.contactPerson || selectedContact.companyName,
                dealerBusinessName: selectedContact.companyName,
                diamondId: diamond.id,
                diamondDetails: {
                    shape: diamond.shape,
                    caratWeight: diamond.caratWeight,
                    color: diamond.color,
                    clarity: diamond.clarity,
                    cut: diamond.cut,
                    lab: diamond.lab,
                    certificateNumber: diamond.certificateNumber,
                    stockNumber: diamond.stockNumber,
                    priceUSD: diamond.priceUSD
                },
                clientName,
                clientContact,
                purpose,
                dueDate: dueDate || undefined,
                dealerNotes: notes
            };
            if (mockDataService.isMockMode()) {
                mockDataService.createMemo(memoData);
                notify.success('Success', 'Memo created successfully');
                onSuccess();
                handleClose();
            }
            else {
                const response = await apiClient.post('memos/create-manual', memoData);
                if (response.ok && response.data?.success) {
                    activityTracker.trackAction('requested-memo', `Memo created for diamond ${diamond.stockNumber || diamond.id}`, {
                        diamondId: diamond.id, stockNumber: diamond.stockNumber, clientName, purpose
                    });
                    notify.success('Success', 'Memo created successfully');
                    onSuccess();
                    handleClose();
                }
                else {
                    notify.error('Error', response.data?.error || 'Failed to create memo');
                }
            }
        }
        catch (error) {
            notify.error('Error', 'Failed to create memo');
        }
        finally {
            setLoading(false);
        }
    };
    const handleClose = () => {
        setSelectedContact(null);
        setContactSearch('');
        setStockNumber('');
        setDiamond(null);
        setClientName('');
        setClientContact('');
        setPurpose('viewing');
        setDueDate('');
        setNotes('');
        onClose();
    };
    if (!isOpen)
        return null;
    return (_jsx(Overlay, { onClick: handleClose, children: _jsxs(Modal, { onClick: e => e.stopPropagation(), children: [_jsxs(Header, { children: [_jsx(Title, { children: "Create New Memo" }), _jsx(CloseButton, { onClick: handleClose, children: _jsx(FaTimes, {}) })] }), _jsxs(Content, { children: [_jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx(FaUser, {}), " Select Dealer/Customer"] }), selectedContact ? (_jsxs(SelectedItem, { children: [_jsxs("div", { className: "info", children: [_jsx("div", { className: "name", children: selectedContact.companyName }), _jsxs("div", { className: "detail", children: [selectedContact.contactPerson, " \u2022 ", selectedContact.email] })] }), _jsx("button", { onClick: () => setSelectedContact(null), children: _jsx(FaTimes, {}) })] })) : (_jsxs(SearchContainer, { children: [_jsx(SearchIcon, { children: _jsx(FaSearch, {}) }), _jsx(SearchInput, { placeholder: "Search contacts by name or email...", value: contactSearch, onChange: e => {
                                                setContactSearch(e.target.value);
                                                setShowContactDropdown(true);
                                            }, onFocus: () => setShowContactDropdown(true) }), showContactDropdown && filteredContacts.length > 0 && (_jsx(Dropdown, { children: filteredContacts.slice(0, 5).map(contact => (_jsxs(DropdownItem, { onClick: () => handleSelectContact(contact), children: [_jsx("div", { className: "name", children: contact.companyName }), _jsxs("div", { className: "detail", children: [contact.contactPerson, " \u2022 ", contact.email] })] }, contact.id))) }))] }))] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: [_jsx(FaGem, {}), " Select Diamond"] }), _jsxs("div", { style: { display: 'flex', gap: '0.5rem' }, children: [_jsx(Input, { placeholder: "Enter stock number (e.g., OP12345)", value: stockNumber, onChange: e => setStockNumber(e.target.value), style: { flex: 1 } }), _jsx(LookupButton, { onClick: handleLookupDiamond, disabled: lookingUp, children: lookingUp ? 'Looking...' : 'Lookup' })] }), diamond && (_jsxs(DiamondPreview, { children: [_jsxs("div", { className: "title", children: [diamond.caratWeight, "ct ", diamond.shape, " ", diamond.color, " ", diamond.clarity] }), _jsxs("div", { className: "details", children: [_jsxs("span", { children: ["Cut: ", diamond.cut] }), _jsxs("span", { children: ["Lab: ", diamond.lab] }), _jsxs("span", { children: ["Stock: ", diamond.stockNumber] })] }), _jsxs("div", { className: "price", children: ["$", diamond.priceUSD?.toLocaleString()] })] }))] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Memo Details" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "End Client Name" }), _jsx(Input, { placeholder: "Customer viewing the diamond", value: clientName, onChange: e => setClientName(e.target.value) })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Client Contact" }), _jsx(Input, { placeholder: "Phone or email", value: clientContact, onChange: e => setClientContact(e.target.value) })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Purpose" }), _jsxs(Select, { value: purpose, onChange: e => setPurpose(e.target.value), children: [_jsx("option", { value: "viewing", children: "Viewing" }), _jsx("option", { value: "appraisal", children: "Appraisal" }), _jsx("option", { value: "consideration", children: "Consideration" }), _jsx("option", { value: "photography", children: "Photography" }), _jsx("option", { value: "other", children: "Other" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Due Date" }), _jsx(Input, { type: "date", value: dueDate, onChange: e => setDueDate(e.target.value) })] })] }), _jsxs(FormGroup, { style: { marginTop: '1rem' }, children: [_jsx(Label, { children: "Notes" }), _jsx(Textarea, { placeholder: "Additional notes about this memo...", value: notes, onChange: e => setNotes(e.target.value) })] })] })] }), _jsxs(Footer, { children: [_jsx(Button, { onClick: handleClose, children: "Cancel" }), _jsx(Button, { primary: true, onClick: handleSubmit, disabled: loading || !selectedContact || !diamond, children: loading ? 'Creating...' : 'Create Memo' })] })] }) }));
};
export default CreateMemoModal;
